import axios, { AxiosInstance } from 'axios';

export interface ServiceFacilityContent {
    id: number;
    title: string;
    description: string;
    image?: string;
    created_at: string;
    updated_at: string;
}

export interface CreateFacilityData {
    title: string;
    description: string;
    image?: File;
}

export interface UpdateFacilityData {
    id: number;
    title?: string;
    description?: string;
    image?: File;
}

class FacilityService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            timeout: 10000,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const storageType = localStorage.getItem('storageType');
                const storage = storageType === 'local' ? localStorage : sessionStorage;
                const token = storage.getItem('token');
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async createFacilityContent(data: CreateFacilityData): Promise<ServiceFacilityContent | undefined> {
        try {
            const formData = new FormData();
            formData.append('title', data.title);
            formData.append('description', data.description);
            
            if (data.image) {
                formData.append('image', data.image);
            }
    
            const response = await this.axiosInstance.post<{
                message: string;
                data: ServiceFacilityContent;
            }>('/admin/facility-content', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to create facility content');
            return undefined;
        }
    }

    async getFacilityContent(): Promise<ServiceFacilityContent[]> {
        try {
            const response = await this.axiosInstance.get<{
                data: ServiceFacilityContent[];
            }>('/facility-content');
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve facility content');
            return [];
        }
    }

    async getFacilityContentById(id: number): Promise<ServiceFacilityContent | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: ServiceFacilityContent;
            }>(`/facility-content/${id}`);
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve facility content');
            return undefined;
        }
    }

    async updateFacilityContent(data: UpdateFacilityData): Promise<ServiceFacilityContent | undefined> {
        try {
            const formData = new FormData();
            
            if (data.title) formData.append('title', data.title);
            if (data.description) formData.append('description', data.description);
            if (data.image) formData.append('image', data.image);
    
            const response = await this.axiosInstance.put<{
                message: string;
                data: ServiceFacilityContent;
            }>(`/admin/facility-content/${data.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to update facility content');
            return undefined;
        }
    }

    async deleteFacilityContent(id: number): Promise<void> {
        try {
            await this.axiosInstance.delete(`/admin/facility-content/${id}`);
        } catch (error) {
            this.handleError(error, 'Failed to delete facility content');
        }
    }

    private handleError(error: unknown, message: string) {
        if (axios.isAxiosError(error)) {
            console.error(`${message}:`, error.response?.data);
            throw error.response?.data || { error: message };
        }
        console.error(message, error);
        throw { error: message };
    }
}

export const facilityService = new FacilityService();